import React from "react";
import { motion } from "framer-motion";
import {
  ClipboardList,
  Coins,
  Wrench,
  FolderUp,
  Factory,
  ArrowRightCircle,
} from "lucide-react";

const CARDS = [
  {
    icon: ClipboardList,
    title: "One Structured Request",
    body:
      "Replace email chaos with a single, complete sourcing request — every requirement captured the first time.",
    accent: "blue",
  },
  {
    icon: Coins,
    title: "Commercial Requirements",
    body:
      "Quantity, target price, Incoterm, payment terms and target delivery date — clearly defined before suppliers see it.",
    accent: "neutral",
  },
  {
    icon: Wrench,
    title: "Technical Specifications",
    body:
      "Capture material, composition, dimensions, tolerances, certifications and inspection scope in one place.",
    accent: "neutral",
  },
  {
    icon: FolderUp,
    title: "Supporting Documents",
    body:
      "Attach tech packs, drawings, brand specs and test reports. Manufacturers receive the full picture instantly.",
    accent: "neutral",
  },
  {
    icon: Factory,
    title: "Factory Direct Quotations",
    body:
      "Reach verified Turkish manufacturers directly — no agents, no markups, no telephone-game pricing.",
    accent: "emerald",
  },
  {
    icon: ArrowRightCircle,
    title: "Ready for CommodityBid™",
    body:
      "Continue any RFQ into a live procurement auction — the same Workspace, the same suppliers, zero re-entry.",
    accent: "blue",
  },
];

const accentMap = {
  blue: "bg-[#0044FF]/8 text-[#0044FF] border-[#0044FF]/15",
  emerald: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  neutral: "bg-black/[0.04] text-black/70 border-black/[0.06]",
};

export default function WhyRfq() {
  return (
    <section
      id="why"
      data-testid="why-rfq-section"
      className="relative py-24 sm:py-32 border-t border-black/[0.06] bg-[#FAFAFB]"
    >
      <div className="absolute inset-0 bg-grid-fine opacity-50 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="eyebrow">Why RFQ™</span>
            <h2 className="mt-4 font-display font-black tracking-[-0.035em] text-[40px] sm:text-[56px] leading-[0.98] text-[#0A0A0A]">
              Sourcing that behaves
              <br />
              <span className="text-[#0044FF]">like procurement.</span>
            </h2>
          </div>
          <p className="md:max-w-md text-[15px] leading-relaxed text-black/60">
            RFQ™ is built around the way professional buyers actually work — structured,
            documented, and connected to the rest of the import operation.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {CARDS.map(({ icon: Icon, title, body, accent }, i) => (
            <motion.article
              key={title}
              data-testid={`why-card-${i}`}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group relative rounded-2xl border border-black/[0.06] bg-white p-6 sm:p-7 hover:border-black/[0.14] hover:shadow-card transition-all duration-300 hover:-translate-y-0.5"
            >
              <div
                className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border ${accentMap[accent]}`}
              >
                <Icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-mono text-[10px] tracking-[0.18em] text-black/40">
                  0{i + 1}
                </span>
                <h3 className="font-display text-[19px] sm:text-[20px] font-semibold tracking-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-2 text-[14px] leading-relaxed text-black/60">{body}</p>

              <div className="absolute inset-x-6 bottom-5 h-px bg-gradient-to-r from-transparent via-black/[0.08] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
